import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  Client,
  ClientCreateFormData,
  ClientUpdateFormData,
} from "../../schemas/clientSchema";

export interface ClientResult {
  clients: Client[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface ClientFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdateClientPayload {
  id: string;
  client: ClientUpdateFormData;
}

const GET_CLIENTS = gql`
  query GetClients($page: Int, $pageSize: Int, $search: String) {
    clients(page: $page, pageSize: $pageSize, search: $search) {
      clients {
        id
        name
        email
        phoneNumber
        restaurantName
        tenantId
        roleId
        createdAt
        updatedAt
        createdBy
        screens {
          id
        }
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_CLIENT_BY_ID = gql`
  query GetClientById($id: ID!) {
    client(id: $id) {
      id
      name
      email
      phoneNumber
      restaurantName
      tenantId
      roleId
      createdAt
      updatedAt
      createdBy
      screens {
        id
      }
    }
  }
`;

const CREATE_CLIENT = gql`
  mutation CreateClient($input: CreateClientInput!) {
    createClient(input: $input)
  }
`;

const UPDATE_CLIENT = gql`
  mutation UpdateClient($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input)
  }
`;

const DELETE_CLIENTS = gql`
  mutation DeleteClients($ids: [ID!]!) {
    deleteClients(ids: $ids)
  }
`;

export const clientService = {
  getAll: async (filters: ClientFilters = {}): Promise<ClientResult> => {
    const { data } = await client.query({
      query: GET_CLIENTS,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.clients;
  },

  getById: async (id: string): Promise<Client> => {
    const { data } = await client.query({
      query: GET_CLIENT_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.client;
  },

  create: async (input: ClientCreateFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_CLIENT,
      variables: { input },
    });
    return data.createClient;
  },

  update: async (payload: UpdateClientPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_CLIENT,
      variables: {
        id: payload.id,
        input: payload.client,
      },
    });
    return data.updateClient;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_CLIENTS,
      variables: { ids },
    });
    return data.deleteClients;
  },
};
